import {
  Card,
  ContextMenuFlyout,
  css,
  Headline,
  HeadlineProps,
  Icon,
  styled,
} from '@zeiss/beyond-online-react';
import { useTranslation } from 'react-i18next';

const StyledCard = styled(Card)`
  box-sizing: border-box;
  width: 100%;
  max-width: none;
  height: 100%;
  padding: var(--spacing-responsive-s) var(--spacing-m)
    var(--spacing-responsive-m);
  overflow: visible;
  overflow-wrap: anywhere;
  box-shadow: none;
`;

interface StyledHeadlineProps extends HeadlineProps {
  status?: ApplicationCardProps['status'];
}

/**
 * We use the 'm' size for the headline so that we can display the eyebrow for
 * the `status` because beyond doesn't support eyebrow for sizes smaller than 'm'.
 *
 * So here we override the style of the main headline span to be size "s" to match
 * our designs:
 *  - When there is no eyebrow it is the first span, otherwise it is the second span
 */
const StyledHeadline = styled(Headline)<StyledHeadlineProps>`
  span:first-child {
    font: var(--text-hl-s);
  }

  ${({ eyebrow, status }) =>
    eyebrow &&
    css`
      span:first-child {
        font: var(--text-eyebrow-s);
        color: ${
          status === 'adding'
            ? 'var(--color-global-text-success)'
            : 'var(--color-global-text-error)'
        };
      }

      span:nth-child(2) {
        font: var(--text-hl-s);
      }
    `};
`;

const CardContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--spacing-s);
`;

const CardContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: var(--spacing-s);
  align-items: stretch;
`;

const DismissButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const DismissButton = styled.button`
  cursor: pointer;
  background: transparent;
  border: none;
`;

const PropertyListContainer = styled.dl`
  margin: 0;
  color: var(--color-global-text-strong);
`;

const PropertyLabel = styled.dt`
  font: var(--text-label-xs);
`;

const PropertyValue = styled.dd`
  margin-inline-start: 0;
  font: var(--text-input);
`;

const StyledContextMenuFlyout = styled(ContextMenuFlyout)`
  /*
    The flyout calculates the position based on the width of the dialog. This ensures
    that the content isn't squished due to the anchors size in the flex container
  */
  div[role='dialog'] {
    width: max-content;
  }
`;

interface ApplicationCardProps {
  /**
   * Determine whether dismiss control is placed behind a confirmation step
   */
  confirmDismiss?: boolean;
  department?: string | null;
  id: string;
  name: string;
  /**
   * Function to call when the 'close' button is clicked or the dismissal is confirmed
   */
  onDismiss?: (id: ApplicationCardProps['id']) => void;
  /**
   * Function to call to undo dismissal.
   */
  onRestore?: (id: ApplicationCardProps['id']) => void;
  /**
   * Display application data only, excluding edit controls (checkbox, dismiss button)
   * @default false
   */
  readonly?: boolean;
  status?: 'adding' | 'removing';
}

export const ApplicationCard = ({
  confirmDismiss,
  department,
  id,
  name,
  onDismiss,
  onRestore,
  readonly,
  status,
}: ApplicationCardProps) => {
  const { t } = useTranslation(['common']);

  const onClick = () => {
    status === 'removing' ? onRestore?.(id) : onDismiss?.(id);
  };

  const eyebrow =
    !readonly && status ? t(`common:action.${status}`) : undefined;

  const contextMenuItemLabel =
    status === 'removing'
      ? t('common:action.undoChange')
      : t('common:action.removeFromX', {
          x: t('common:glossary.applicationList'),
        });

  return (
    <StyledCard>
      <CardContainer>
        <CardContent>
          <StyledHeadline
            eyebrow={eyebrow}
            headline={name}
            size="m"
            status={status}
          />

          <PropertyListContainer>
            <PropertyLabel>{t('common:glossary.department')}</PropertyLabel>
            <PropertyValue>{department}</PropertyValue>
          </PropertyListContainer>
        </CardContent>

        {!readonly ? (
          confirmDismiss ? (
            <StyledContextMenuFlyout
              enforcePosition
              groups={[
                [
                  {
                    label: contextMenuItemLabel,
                    onClick,
                  },
                ],
              ]}
              position="left"
            />
          ) : (
            <DismissButtonContainer>
              <DismissButton onClick={onClick}>
                <Icon icon="Close" size={16} />
              </DismissButton>
            </DismissButtonContainer>
          )
        ) : null}
      </CardContainer>
    </StyledCard>
  );
};
